import { useMutation, type ApolloError } from '@apollo/client'
import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  PREFILL_CART,
  type IPrefillCartMutation,
  type IPrefillCartMutationVariables,
  type PrefillCartLine,
} from '@commerce/core'

export interface UsePrefillCart {
  /** Resolves true when the whole basket landed, false when it did not. */
  addBasket: (lines: readonly PrefillCartLine[]) => Promise<boolean>
  error: ApolloError | undefined
  loading: boolean
}

/**
 * Adds a whole suggested basket in one call.
 *
 * "Agregar todo" used to loop `cartAddProduct` over the lines: five lines meant
 * five mutations racing each other, five server-side recalculations of the same
 * cart, and four intermediate carts — with four intermediate promotion states —
 * that no shopper ever had. A partial failure left the cart holding some of the
 * basket with nothing saying which part.
 *
 * `prefillCart` is the mutation the API has for exactly this, and it returns the
 * resulting cart, so — like `cartAddProduct` — Apollo writes it onto the
 * normalised `Cart:<id>` entity and every open query repaints without a refetch.
 *
 * `replaceCart: false`: the shopper is adding a suggestion to what they already
 * have. `true` would throw their cart away, which is not what the button says.
 */
export const usePrefillCart = (): UsePrefillCart => {
  const { slug = '', sessionId = '' } = useParams()
  const [mutate, { error, loading }] = useMutation<
    IPrefillCartMutation,
    IPrefillCartMutationVariables
  >(PREFILL_CART, { onError: () => undefined })

  const addBasket = useCallback(
    async (lines: readonly PrefillCartLine[]) => {
      if (lines.length === 0) return false
      const result = await mutate({
        variables: {
          storefrontName: slug,
          sessionUid: sessionId,
          items: lines.map((line) => ({ sku: line.sku, quantity: line.quantity })),
          replaceCart: false,
        },
      })
      // `onError` above turns a failure into a resolved result with no data,
      // which is what makes this a boolean rather than a thrown error the
      // caller would have to catch to avoid an unhandled rejection.
      return Boolean(result?.data?.prefillCart)
    },
    [mutate, slug, sessionId],
  )

  return { addBasket, error, loading }
}
